import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useTour from "../../hooks/useTour";
import useBreakpoint from "../../hooks/useBreakpoint";
import Button from "../buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowLeft,
  faForward,
  faMinus,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";

const TOOLTIP_MARGIN = 16;

const TourTooltip = ({ spotlightRect, isCentered }) => {
  const { t } = useTranslation();
  const {
    stepData,
    currentSection,
    currentStep,
    totalStepsInSection,
    sectionCount,
    sections,
    singleSectionMode,
    hideTooltip,
    nextStep,
    prevStep,
    skipSection,
    skipTour,
  } = useTour();
  const { isLtTablet } = useBreakpoint();
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  const isMobile = isLtTablet();
  const tPrefix = "dashboard.tour.";

  useEffect(() => {
    if (tooltipRef.current) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      setTooltipSize({ width: offsetWidth, height: offsetHeight });
    }
  }, [stepData, isCentered]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        nextStep();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevStep();
      } else if (e.key === "Escape") {
        e.preventDefault();
        hideTooltip();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nextStep, prevStep, hideTooltip]);

  const tooltipStyle = useMemo(() => {
    if (isCentered || isMobile || !spotlightRect) return {};

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const placement = stepData?.placement || "bottom";
    const style = { position: "fixed" };

    const centerX = spotlightRect.left + spotlightRect.width / 2;
    const centerY = spotlightRect.top + spotlightRect.height / 2;

    switch (placement) {
      case "bottom":
        style.top = spotlightRect.top + spotlightRect.height + TOOLTIP_MARGIN;
        style.left = Math.max(
          TOOLTIP_MARGIN,
          Math.min(
            centerX - tooltipSize.width / 2,
            vw - tooltipSize.width - TOOLTIP_MARGIN,
          ),
        );
        break;
      case "top":
        style.top =
          spotlightRect.top - tooltipSize.height - TOOLTIP_MARGIN;
        style.left = Math.max(
          TOOLTIP_MARGIN,
          Math.min(
            centerX - tooltipSize.width / 2,
            vw - tooltipSize.width - TOOLTIP_MARGIN,
          ),
        );
        break;
      case "left":
        style.top = Math.max(
          TOOLTIP_MARGIN,
          Math.min(
            centerY - tooltipSize.height / 2,
            vh - tooltipSize.height - TOOLTIP_MARGIN,
          ),
        );
        style.left =
          spotlightRect.left - tooltipSize.width - TOOLTIP_MARGIN;
        break;
      case "right":
        style.top = Math.max(
          TOOLTIP_MARGIN,
          Math.min(
            centerY - tooltipSize.height / 2,
            vh - tooltipSize.height - TOOLTIP_MARGIN,
          ),
        );
        style.left =
          spotlightRect.left + spotlightRect.width + TOOLTIP_MARGIN;
        break;
      default:
        break;
    }

    // Keep tooltip in viewport
    if (style.top < TOOLTIP_MARGIN) style.top = TOOLTIP_MARGIN;
    if (style.left < TOOLTIP_MARGIN) style.left = TOOLTIP_MARGIN;
    if (style.top + tooltipSize.height > vh - TOOLTIP_MARGIN) {
      style.top = vh - tooltipSize.height - TOOLTIP_MARGIN;
    }
    if (style.left + tooltipSize.width > vw - TOOLTIP_MARGIN) {
      style.left = vw - tooltipSize.width - TOOLTIP_MARGIN;
    }

    return style;
  }, [isCentered, isMobile, spotlightRect, stepData, tooltipSize]);

  if (!stepData) return null;

  const sectionTitle = t(sections[currentSection]?.tKey);
  const stepTitle = t(`${tPrefix}${stepData.titleKey}`);
  const stepContent = t(`${tPrefix}${stepData.contentKey}`);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalStepsInSection - 1;

  const containerClass = [
    "tour-tooltip",
    isCentered ? "tour-tooltip--centered" : "tour-tooltip--positioned",
    isMobile ? "tour-tooltip--mobile" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass} style={tooltipStyle} ref={tooltipRef}>
      <div className="tour-tooltip__header">
        <span className="tour-tooltip__section-label">
          {!singleSectionMode && `${currentSection + 1}/${sectionCount} — `}
          {sectionTitle}
        </span>
        <div className="flex gap-1">
          <Button
            variant="light"
            icon
            onClick={hideTooltip}
            title={t(`${tPrefix}controls.hide`)}
          >
            <FontAwesomeIcon icon={faMinus} />
          </Button>
          <Button
            variant="light"
            icon
            onClick={skipTour}
            title={t(`${tPrefix}controls.skipTour`)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>
      </div>

      <div className="tour-tooltip__body">
        <h4 className="tour-tooltip__title">{stepTitle}</h4>
        <p className="tour-tooltip__content">{stepContent}</p>
      </div>

      <div className="tour-tooltip__footer">
        <div className="tour-tooltip__steps">
          {Array.from({ length: totalStepsInSection }).map((_, i) => (
            <span
              key={i}
              className={`tour-tooltip__dot ${i === currentStep ? "tour-tooltip__dot--active" : ""} ${i < currentStep ? "tour-tooltip__dot--done" : ""}`}
            />
          ))}
        </div>

        <div className="tour-tooltip__actions">
          {!singleSectionMode && (
            <Button variant="light" size="sm" onClick={skipSection}>
              <FontAwesomeIcon icon={faForward} className="mr-2" />
              <span>{t(`${tPrefix}controls.skipSection`)}</span>
            </Button>
          )}
          {!isFirstStep && (
            <Button variant="light" size="sm" onClick={prevStep}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={nextStep}>
            <span className="mr-2">
              {isLastStep
                ? t(`${tPrefix}controls.${singleSectionMode ? "finish" : "nextSection"}`)
                : t(`${tPrefix}controls.next`)}
            </span>
            <FontAwesomeIcon icon={faArrowRight} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourTooltip;
